import { useEffect, useState } from "react";
import { Button, Divider, Flex, Form, Typography, Image, Switch, Dropdown, message } from "antd";
import { mapValues, unionBy } from "lodash";
import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";

import filterProjectsIcon from "../../assets/images/filter-projects.svg";
import paymentMethodIcon from "../../assets/images/payment-method.svg";
import projectStatusIcon from "../../assets/images/project-status.svg";
import salesPersonIcon from "../../assets/images/sales-person.svg";
import subcontractorIcon from "../../assets/images/subcontractor.svg";
import utilityProgramIcon from "../../assets/images/utility-program.svg";
import { MultiSelectBox } from "shared/components";
import { useLookupOptions, useAuditorSelectOptions, useAccountManagerSelectOptions } from "admin/queries";
import { useSubcontractors } from "subcontractors/queries";
import { IProjectFilter } from "projects/models";
import { useBoundStore } from "store";
import { http } from "shared/api/http";
import "./ProjectFilters.scss";

export const ProjectFilters = () => {
  const [form] = Form.useForm();
  const [exporting, setExporting] = useState(false);
  const {
    setFilters,
    clearFilters,
    hvacSubcontractorsSelectOptions,
    lightingSubcontractorsSelectOptions,
    refrigerationSubcontractorsSelectOptions,
    setSubcontractorsSelectOptions,
    setShowArchived,
    filters,
    showArchived,
    stage,
    tenant,
    currentOrganization,
  } = useBoundStore();

  // Fetch subcontractors and populate store options
  const { data: subcontractorsData } = useSubcontractors();
  useEffect(() => {
    if (subcontractorsData) {
      setSubcontractorsSelectOptions(subcontractorsData);
    }
  }, [subcontractorsData, setSubcontractorsSelectOptions]);

  // Fetch auditor and account manager options from API
  const { data: auditorSelectOptions = [] } = useAuditorSelectOptions();
  const { data: accountManagerOptions = [] } = useAccountManagerSelectOptions();

  // Fetch lookup options from API
  const { data: utilityProgramOptions = [] } = useLookupOptions("utility_program");
  const { data: paymentMethodOptions = [] } = useLookupOptions("customer_payment_method");
  const { data: projectStatusOptions = [] } = useLookupOptions("project_status");

  const handleExport = async (format: "csv" | "xlsx", includeAllData = false) => {
    if (!tenant?.id || !currentOrganization?.id) {
      message.error("Unable to export: missing tenant or organization");
      return;
    }

    // Build query params from current filters
    const params = new URLSearchParams();
    params.set("format", format);
    if (filters?.projectStatus?.length) {
      params.set("status", filters.projectStatus.join(","));
    }
    if (filters?.subcontractor?.length) {
      params.set("subcontractor", filters.subcontractor.join(","));
    }
    if (filters?.utilityProgram?.length) {
      params.set("utilityProgram", filters.utilityProgram.join(","));
    }
    if (filters?.accountManager?.length) {
      params.set("accountManager", filters.accountManager.join(","));
    }
    if (filters?.auditor?.length) {
      params.set("auditor", filters.auditor.join(","));
    }
    if (filters?.customerPaymentMethod?.length) {
      params.set("customerPaymentMethod", filters.customerPaymentMethod.join(","));
    }
    if (stage) {
      params.set("stage", stage);
    }
    if (showArchived) {
      params.set("showArchived", "true");
    }
    if (includeAllData) {
      params.set("includeAllData", "true");
    }

    const exportUrl = `/api/v1/tenants/${tenant.id}/organizations/${currentOrganization.id}/projects/export?${params.toString()}`;

    try {
      setExporting(true);
      const response = await http.get(exportUrl, {
        responseType: "blob",
      });

      // Set MIME type based on format
      const mimeType = format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "text/csv";
      const extension = format === "xlsx" ? "xlsx" : "csv";

      // Create a download link for the blob
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const suffix = includeAllData ? "-all-fields" : "";
      link.download = `projects-export${suffix}-${new Date().toISOString().split("T")[0]}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success("Export completed successfully");
    } catch (error) {
      message.error("Failed to export projects. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const exportMenuItems: MenuProps["items"] = [
    { key: "csv", label: "Export as CSV", onClick: () => handleExport("csv") },
    { key: "xlsx", label: "Export as Excel", onClick: () => handleExport("xlsx") },
    { type: "divider" },
    { key: "csv-all", label: "Export as CSV (All Fields)", onClick: () => handleExport("csv", true) },
    { key: "xlsx-all", label: "Export as Excel (All Fields)", onClick: () => handleExport("xlsx", true) },
  ];

  const clearFiltersHandler = () => {
    form.setFieldsValue({
      projectStatus: [],
      subcontractor: [],
      utilityProgram: [],
      accountManager: [],
      auditor: [],
      customerPaymentMethod: [],
    });
    clearFilters();
  };

  const onValuesChangeHandler = async (
    changedValues: any,
    allValues: IProjectFilter
  ) => {
    const filters = mapValues(allValues, (x) => x ?? []);
    setFilters(filters);
  };

  const onShowArchivedSwitchChangeHandler = (checked: boolean) => {
    setShowArchived(checked);
    // If turning off, remove "Archived" from the projectStatus filter
    if (!checked && filters?.projectStatus?.includes("Archived")) {
      const newStatus = filters.projectStatus.filter(s => s !== "Archived");
      form.setFieldsValue({ projectStatus: newStatus });
      setFilters({ ...filters, projectStatus: newStatus });
    }
  };

  // Only show "Archived" in status dropdown when the toggle is enabled
  const filteredProjectStatuses = showArchived
    ? projectStatusOptions
    : projectStatusOptions.filter((s) => s.value !== "Archived");

  return (
    <>
      <Flex gap='middle'>
        <Flex gap='small'>
          <Image
            src={filterProjectsIcon}
            alt='Filter Projects'
            preview={false}
            width={20}
          />
          <Typography.Title className='project-filters-title' level={2}>
            Filter Projects
          </Typography.Title>
        </Flex>
        <Button ghost type='primary' size='small' onClick={clearFiltersHandler}>
          Clear all filters
        </Button>
        <Dropdown menu={{ items: exportMenuItems }} trigger={["click"]}>
          <Button
            type='primary'
            size='small'
            icon={<DownloadOutlined />}
            loading={exporting}
          >
            Export <DownOutlined />
          </Button>
        </Dropdown>
        <Flex gap='middle' className='project-filters-show-archived-switch'>
          <Typography.Text>Show Archived</Typography.Text>
          <Switch
            defaultValue={showArchived}
            onChange={onShowArchivedSwitchChangeHandler}
          />
        </Flex>
      </Flex>
      <Divider />
      <Form
        form={form}
        name='projectFiltersForm'
        layout='vertical'
        initialValues={{
          projectStatus: [...filters?.projectStatus],
          subcontractor: [...filters?.subcontractor],
          utilityProgram: [...filters?.utilityProgram],
          accountManager: [...filters?.accountManager],
          auditor: [...filters?.auditor],
          customerPaymentMethod: [...filters?.customerPaymentMethod],
        }}
        onValuesChange={onValuesChangeHandler}
      >
        <Flex justify='space-between'>
          <MultiSelectBox
            options={filteredProjectStatuses}
            name='projectStatus'
            label={
              <Flex gap='small'>
                <Image
                  src={projectStatusIcon}
                  alt='Project Status'
                  preview={false}
                  width={20}
                />
                Project Status
              </Flex>
            }
          />
          <MultiSelectBox
            options={unionBy(
              [
                ...hvacSubcontractorsSelectOptions,
                ...lightingSubcontractorsSelectOptions,
                ...refrigerationSubcontractorsSelectOptions,
              ],
              "value"
            )}
            name='subcontractor'
            label={
              <Flex gap='small'>
                <Image
                  src={subcontractorIcon}
                  alt='Subcontractor'
                  preview={false}
                  width={20}
                />
                Subcontractor
              </Flex>
            }
          />
          <MultiSelectBox
            options={utilityProgramOptions}
            name='utilityProgram'
            label={
              <Flex gap='small'>
                <Image
                  src={utilityProgramIcon}
                  alt='Utility Program'
                  preview={false}
                  width={20}
                />
                Utility Program
              </Flex>
            }
          />
          <MultiSelectBox
            options={accountManagerOptions}
            name='accountManager'
            label={
              <Flex gap='small'>
                <Image
                  src={salesPersonIcon}
                  alt='Account Manager'
                  preview={false}
                  width={20}
                />
                Account Manager
              </Flex>
            }
          />
          <MultiSelectBox
            options={auditorSelectOptions}
            name='auditor'
            label={
              <Flex gap='small'>
                <Image
                  src={salesPersonIcon}
                  alt='Auditor'
                  preview={false}
                  width={20}
                />
                Auditor
              </Flex>
            }
          />
          <MultiSelectBox
            options={paymentMethodOptions}
            name='customerPaymentMethod'
            label={
              <Flex gap='small'>
                <Image
                  src={paymentMethodIcon}
                  alt='Customer Payment Method'
                  preview={false}
                  width={20}
                />
                Customer Payment Method
              </Flex>
            }
          />
        </Flex>
      </Form>
    </>
  );
};
